import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Plus, Send, Star, MessageSquare, X, RefreshCw, Sparkles } from "lucide-react";

const CAT = { technical: "فني", accounting: "محاسبي", billing: "فوترة", suggestion: "اقتراح" };
const PRI = { low: { l: "منخفض", c: "bg-slate-100 text-slate-700" }, normal: { l: "عادي", c: "bg-blue-100 text-blue-700" }, high: { l: "مهم", c: "bg-amber-100 text-amber-700" }, critical: { l: "حرج", c: "bg-red-100 text-red-700" } };
const ST = { open: { l: "مفتوح", c: "bg-emerald-100 text-emerald-700" }, in_progress: { l: "قيد المعالجة", c: "bg-blue-100 text-blue-700" }, waiting: { l: "بانتظار الرد", c: "bg-amber-100 text-amber-700" }, closed: { l: "مغلق", c: "bg-slate-100 text-slate-500" } };

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [view, setView] = useState(null);
  const [reply, setReply] = useState("");
  const [rating, setRating] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggest, setAiSuggest] = useState("");
  const [form, setForm] = useState({ subject: "", description: "", category: "technical", priority: "normal" });

  const load = async () => { try { const { data } = await api.get("/tickets"); setItems(data); } catch (e) { toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.subject.trim()) return toast.error("العنوان مطلوب");
    try { await api.post("/tickets", form); toast.success("تم إنشاء التذكرة"); setDlg(false); setForm({ subject: "", description: "", category: "technical", priority: "normal" }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openView = async (t) => { try { const { data } = await api.get(`/tickets/${t.id}`); setView(data); setReply(""); setRating(data.rating || 0); setAiSuggest(""); } catch (e) { toast.error(formatErr(e)); } };

  const askAi = async () => {
    if (!view) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/ticket-suggest/${view.id}`);
      setAiSuggest(data.suggestion || "لم يستطع المساعد توليد اقتراح.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAiLoading(false); }
  };
  const useAiSuggest = () => { if (aiSuggest) setReply(aiSuggest); setAiSuggest(""); };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/tickets/${view.id}/messages`, { message: reply }); setReply(""); openView(view); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const closeTicket = async () => {
    try { await api.post(`/tickets/${view.id}/status`, { status: "closed" }); toast.success("تم إغلاق التذكرة"); openView(view); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const submitRating = async () => {
    if (rating < 1) return toast.error("اختر تقييماً");
    try { await api.post(`/tickets/${view.id}/rate`, { rating, comment: "" }); toast.success("شكراً لتقييمك!"); openView(view); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-primary" /> تذاكر الدعم</h1>
          <p className="text-sm text-muted-foreground mt-1">تواصل مع فريق الدعم — الرد خلال 4-24 ساعة</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 me-1" /> تحديث</Button>
          <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-ticket-btn"><Plus className="w-4 h-4" /> تذكرة جديدة</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <LifeBuoy className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد تذاكر</div>
          <Button variant="link" onClick={() => setDlg(true)}>افتح أول تذكرة</Button>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b"><tr><th className="text-start p-3">العنوان</th><th className="text-center p-3">التصنيف</th><th className="text-center p-3">الأولوية</th><th className="text-center p-3">الحالة</th><th className="text-start p-3">التاريخ</th><th className="text-center p-3">إجراء</th></tr></thead>
            <tbody>{items.map(t => (
              <tr key={t.id} className="border-b hover:bg-muted/30" data-testid={`ticket-${t.id}`}>
                <td className="p-3 font-medium">{t.subject}</td>
                <td className="p-3 text-center text-xs">{CAT[t.category]}</td>
                <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs ${PRI[t.priority]?.c}`}>{PRI[t.priority]?.l}</span></td>
                <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs ${ST[t.status]?.c}`}>{ST[t.status]?.l}</span></td>
                <td className="p-3 text-xs font-mono text-muted-foreground">{new Date(t.created_at).toLocaleString("ar-EG")}</td>
                <td className="p-3 text-center"><Button size="sm" variant="outline" onClick={() => openView(t)}><MessageSquare className="w-3.5 h-3.5 me-1" /> عرض</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="new-ticket-dialog">
          <DialogHeader><DialogTitle>تذكرة جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>العنوان</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" /></div>
            <div><Label>الوصف</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="اشرح المشكلة بالتفصيل..." data-testid="ticket-desc" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>التصنيف</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>الأولوية</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRI).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={create} data-testid="submit-ticket-btn"><Send className="w-4 h-4 me-1" /> إرسال</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={o => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-ticket-dialog">
          {view && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{view.subject} <span className={`text-xs px-2 py-0.5 rounded ${ST[view.status]?.c}`}>{ST[view.status]?.l}</span></DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="p-3 rounded bg-muted/30 border text-sm">{view.description}</div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(view.messages || []).map(m => (
                  <div key={m.id} className={`p-2 rounded ${m.sender_role === "super_admin" ? "bg-primary/10 border-r-2 border-primary" : "bg-slate-50 border-r-2 border-slate-300"}`}>
                    <div className="text-xs font-bold flex items-center gap-1">{m.sender_name} {m.sender_role === "super_admin" && <span className="text-[10px] bg-primary text-primary-foreground px-1 rounded">دعم</span>}</div>
                    <div className="text-sm mt-1">{m.message}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(m.at).toLocaleString("ar-EG")}</div>
                  </div>
                ))}
                {(view.messages || []).length === 0 && <p className="text-xs text-center text-muted-foreground py-2">لا توجد ردود بعد</p>}
              </div>
              {view.status !== "closed" && (
                <>
                  <div className="flex gap-2">
                    <Textarea value={reply} onChange={e => setReply(e.target.value)} rows={2} placeholder="اكتب ردك..." data-testid="reply-input" />
                    <div className="flex flex-col gap-1">
                      <Button onClick={sendReply} disabled={!reply.trim()} data-testid="send-reply-btn"><Send className="w-4 h-4" /></Button>
                      <Button onClick={askAi} variant="outline" size="icon" disabled={aiLoading} title="اقتراح ذكي من AI" data-testid="ai-suggest-btn"><Sparkles className={`w-4 h-4 ${aiLoading ? "animate-pulse text-primary" : ""}`} /></Button>
                    </div>
                  </div>
                  {aiSuggest && (
                    <div className="p-3 rounded border border-primary/30 bg-primary/5 text-sm space-y-2" data-testid="ai-suggest-box">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-primary"><Sparkles className="w-3.5 h-3.5" /> اقتراح المساعد الذكي</div>
                      <div className="whitespace-pre-wrap">{aiSuggest}</div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={useAiSuggest} data-testid="use-ai-btn">استخدم كرد</Button>
                        <Button size="sm" variant="ghost" onClick={() => setAiSuggest("")}>تجاهل</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {view.status === "closed" && !view.rating && (
                <div className="p-3 border rounded bg-amber-50">
                  <Label className="mb-2 block">قيّم تجربتك:</Label>
                  <div className="flex gap-1 mb-2">{[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}><Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>
                  ))}</div>
                  <Button size="sm" onClick={submitRating} disabled={!rating}>أرسل التقييم</Button>
                </div>
              )}
              {view.rating && (
                <div className="p-2 rounded bg-emerald-50 border-emerald-200 border text-sm">تقييمك: {[...Array(view.rating)].map((_, i) => <Star key={i} className="w-4 h-4 inline fill-amber-400 text-amber-400" />)}</div>
              )}
            </div>
            <DialogFooter>
              {view.status !== "closed" && <Button variant="outline" onClick={closeTicket} data-testid="close-ticket-btn"><X className="w-4 h-4 me-1" /> إغلاق التذكرة</Button>}
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
